import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import _isString from 'lodash/isString';
import _range from 'lodash/range';
import { reduxFormPropTypes, intlMessage } from 'constants/propTypes';
import { bem } from 'helpers/class-name';
import { useRefDidMount } from 'helpers/react-hooks';
import { shouldShowError } from 'helpers/page-utils';
import InputLabel from '../InputLabel/InputLabel';
import InputError from '../InputError/InputError';
import Textarea from '../../Textarea';
import './Input.scss';

const { block, element } = bem('input-field'); // also used in components/Form/PhoneNumber

// TODO(intl): make sure all placeholders are intl-messages
const Input = forwardRef((props, ref) => {
  const intl = useIntl();
  const {
    input,
    meta,
    className,
    type = 'text',
    label,
    placeholder = '',
    autoFocus,
    required,
    optional,
    showErrorImmediately = false,
    inputClassName,
    heightInLines = 8,
    children,
    ...restInputProps
  } = props;
  const { name } = input;
  const isTextarea = type === 'textarea';
  const hasError = shouldShowError(meta, showErrorImmediately);

  const inputRef = useRefDidMount(node => {
    if (autoFocus) {
      node.focus();
    }
  });

  const commonProps = {
    ref: ref || inputRef,
    type: isTextarea ? 'text' : type,
    id: name,
    ...input,
    ...restInputProps,
    placeholder: _isString(placeholder) ? placeholder : intl.formatMessage(placeholder),
    required,
  };

  return (
    <div className={block(className)}>
      <InputLabel name={name} message={label} required={required} optional={optional} />
      {isTextarea ? (
        <Textarea {...commonProps} hasError={hasError} heightInLines={heightInLines} className={inputClassName} />
      ) : (
        <input {...commonProps} className={element('input', { hasError }, inputClassName)} />
      )}
      {children}
      <InputError {...meta} showErrorImmediately={showErrorImmediately} />
    </div>
  );
});

Input.propTypes = {
  autoFocus: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
  heightInLines: PropTypes.oneOf(_range(2, 11)),
  input: reduxFormPropTypes.field.input,
  inputClassName: PropTypes.string,
  label: intlMessage,
  meta: reduxFormPropTypes.field.meta,
  optional: PropTypes.bool,
  placeholder: PropTypes.oneOfType([PropTypes.string, intlMessage]),
  ref: PropTypes.func,
  required: PropTypes.bool,
  showErrorImmediately: PropTypes.bool,
  type: PropTypes.string,
};

Input.displayName = 'Input';

export default Input;
